import React from 'react';
import styled from 'styled-components';
import {
  People as PeopleIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  DriveEta as DriveEtaIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import Avatar from '../../../components/common/Avatar';
import Navigation from '../../../components/portal/Navigation';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';
import { apiService, API_BASE_URL } from '../../../services/api';
import { useTranslation } from 'react-i18next';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;
const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--desktop-page-padding-x, 2rem);
  padding-top: 40px;

  @media (max-width: 768px) {
    padding: 1rem var(--mobile-page-padding-x);
    padding-top: 32px;
  }
`;
const ContentCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.06);
  margin-bottom: 2rem;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;
const ListItem = styled.li`
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  border: 1px solid var(--card-border, rgba(102, 126, 234, 0.15));
  background: var(--card-bg, #fff);
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(0,0,0,0.06); }
  @media (max-width: 700px) { align-items: flex-start; flex-direction: column; }
`;

const ItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-width: 0;
  flex: 1;

  .avatar-circle {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--primary-orange), var(--primary-blue));
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1rem;
    flex-shrink: 0;
    box-shadow: 0 4px 10px rgba(0,0,0,0.1);
  }

  .details {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
    width: 100%;
  }

  .name-row {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    flex-wrap: wrap;
  }

  .name {
    font-size: 1rem;
    font-weight: 800;
    color: #222;
  }

  .badge {
    font-size: 0.7rem;
    padding: 0.15rem 0.45rem;
    border-radius: 6px;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-weight: 700;
    text-transform: uppercase;
  }

  .cbr-badge {
    background: #f1f3f4;
    color: #666;
  }

  .license-badge {
    background: rgba(102, 126, 234, 0.1);
    color: var(--primary-blue);
  }

  .info-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
    color: #666;
    font-size: 0.85rem;
  }

  .info-item {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }

  @media (max-width: 700px) {
    gap: 0.8rem;
    .avatar-circle { width: 40px; height: 40px; font-size: 1rem; }
  }
`;

const TeacherStudents: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [students, setStudents] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string|null>(null);
  const [q, setQ] = React.useState<string>('');

  const load = React.useCallback(async ()=>{
    setLoading(true); setError(null);
    try {
      const resp = await apiService.get<any>(API_ENDPOINTS.TEACHER.STUDENTS);
      const arr = Array.isArray((resp as any)?.data) ? (resp as any).data : (Array.isArray(resp) ? resp : []);
      setStudents(arr);
    } catch (e:any){ setError(e?.message || t('portal.teacher.students.error_loading', 'Failed to load students')); }
    finally{ setLoading(false); }
  },[]);

  React.useEffect(()=>{ load(); },[load]);

  const filtered = React.useMemo(()=>{
    const term = q.trim().toLowerCase();
    if (!term) return students;
    return students.filter((s:any)=>{
      const first = String(s.firstName||'').toLowerCase();
      const last = String(s.lastName||'').toLowerCase();
      const username = String(s.username||'').toLowerCase();
      const email = String(s.email||'').toLowerCase();
      const full = `${first} ${last}`.trim();
      return first.includes(term) || last.includes(term) || full.includes(term) || username.includes(term) || email.includes(term);
    });
  }, [q, students]);

  return (
    <PageWrapper>
      <Navigation />
      <ContentWrapper>
        <ContentCard>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <PeopleIcon style={{ color: 'var(--primary-blue)' }} />
              <h3 style={{ margin: 0, color: '#222' }}>{t('portal.teacher.students.title', 'My Students')} ({filtered.length})</h3>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative' }}>
                <SearchIcon style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#888', fontSize: 18 }} />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t('portal.teacher.students.search_placeholder', 'Search students...') as string}
                  style={{ padding: '.5rem .6rem .5rem 2.2rem', borderRadius: 10, border: '1px solid rgba(0,0,0,0.1)', background: '#f9f9f9', color: '#222', width: 220 }}
                />
              </div>
              <button onClick={load} style={{ border: 0, borderRadius: 10, padding: '.55rem 1rem', fontWeight: 800, color: '#fff', background: 'linear-gradient(135deg,#667eea,#764ba2)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <RefreshIcon fontSize="small" /> {t('portal.common.refresh', 'Refresh')}
              </button>
            </div>
          </div>
          {loading ? (
            <div style={{ padding: '1rem', color: '#555' }}>{t('portal.teacher.students.loading', 'Loading students…')}</div>
          ) : error ? (
            <div style={{ padding: '1rem', color: '#b00020' }}>{error}</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '1.25rem', textAlign: 'center', color: '#666' }}>
              <div style={{ fontSize: '2rem', opacity: 0.35, marginBottom: 8 }}>📭</div>
              {t('portal.teacher.students.none_found', 'No students found')}
            </div>
          ) : (
            <ul style={{ margin: 0, padding: 0 }}>
              {filtered.map((s: any) => (
                <ListItem key={s.id} onClick={() => navigate(`/portal/teacher/student/${s.id}`)}>
                  <ItemContent>
                    <Avatar 
                      size={48} 
                      src={`${API_BASE_URL}/api/users/${s.id}/avatar`}
                      initials={(s.firstName?.[0] || s.username?.[0] || 'S').toUpperCase()} 
                      rounded={true}
                    />
                    <div className="details">
                      <div className="name-row">
                        <div className="name">{`${(s.firstName || '').trim()} ${(s.lastName || '').trim()}`.trim() || s.username || s.email}</div>
                        {s.houseNumber && (
                          <div className="badge cbr-badge" title={t('portal.register.house_number', 'Huisnummer') as string}>
                            <BadgeIcon sx={{ fontSize: 12 }} /> {s.houseNumber}
                          </div>
                        )}
                        {s.licenseType && (
                          <div className="badge license-badge" title={t('portal.common.license_type', 'License Type') as string}>
                            <DriveEtaIcon sx={{ fontSize: 12 }} /> {s.licenseType}
                          </div>
                        )}
                      </div>
                      <div className="info-row">
                        <div className="info-item">
                          <EmailIcon sx={{ fontSize: 14 }} /> {s.email}
                        </div>
                        {s.phoneNumber && (
                          <div className="info-item">
                            <PhoneIcon sx={{ fontSize: 14 }} /> {s.phoneNumber}
                          </div>
                        )}
                        {s.address && (
                          <div className="info-item">
                            <LocationOnIcon sx={{ fontSize: 14 }} /> {s.address}
                          </div>
                        )}
                      </div>
                    </div>
                  </ItemContent>
                </ListItem>
              ))}
            </ul>
          )}
        </ContentCard>
      </ContentWrapper>
    </PageWrapper>
  );
};

export default TeacherStudents;
